import React, { useState } from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import { CheckIcon, LocateOffIcon } from 'lucide-react';
import { Reveal } from './Reveal';
import { QrCode } from './QrCode';
import { appEvents } from '../data/appEvents';
const ease = [0.23, 1, 0.32, 1] as const;
export function WhyAvega() {
  return <section id="why" className="py-20 lg:py-24" aria-labelledby="why-title" data-id="element-416" data-mp="365hutv39c9dq">
      <div className="mx-auto max-w-7xl px-5 lg:px-8" data-id="element-417" data-mp="7l9lrtwrkqdt">
        <Reveal className="max-w-2xl" data-id="element-418" data-mp="7jyhzu3bjimp">
          <h2 id="why-title" className="font-display text-4xl font-bold leading-[1.05] tracking-[-0.03em] text-ink sm:text-5xl" data-id="element-419" data-mp="2my8tab69zrd">
            More from the events you already love.
          </h2>
          <p className="mt-5 text-lg leading-relaxed text-muted" data-id="element-420" data-mp="3otwo6p11rzhp">
            AVEGA adds something extra to your day out, without getting in the way of it.
          </p>
        </Reveal>

        <div className="mt-10 grid gap-4 lg:grid-cols-3" data-id="element-421" data-mp="2dehqtk4xf5m2">
          <EventsTile data-id="element-422" data-mp="1kxyo8x6hgw7m" />
          <SpeedTile data-id="element-423" data-mp="1kyqia3kfmlvs" />
          <RewardsTile data-id="element-424" data-mp="1ky8d2hfskxbq" />
          <CheckInTile data-id="element-425" data-mp="1kvuliikbm38x" />
        </div>
      </div>
    </section>;
}
const filters = ['All', 'Consumer fair', 'Festival', 'Food'];
function EventsTile() {
  const [filter, setFilter] = useState('All');
  const visible = filter === 'All' ? appEvents : appEvents.filter(e => e.category === filter);
  return <article className="leaf-md flex flex-col bg-white p-7 ring-1 ring-line lg:col-span-2 lg:p-9" data-id="element-426" data-mp="1277i04hsllxg">
      <h3 className="font-display text-2xl font-bold tracking-tight text-ink" data-id="element-427" data-mp="16xxm7eehfiqk">Events that fit you</h3>
      <p className="mt-2 max-w-md text-muted" data-id="element-428" data-mp="s99u5i3uhr5d">
        Browse by date, city and category. No account needed just to look around.
      </p>
      <div className="mt-auto pt-8" data-id="element-429" data-mp="x8w7drjob7e6">
        <div className="flex flex-wrap gap-2" role="group" aria-label="Filter sample events" data-id="element-430" data-mp="x605js4pfykg">
          {filters.map(f => <button key={f} type="button" aria-pressed={filter === f} onClick={() => setFilter(f)} className={`h-9 rounded-full px-4 text-sm font-medium transition-colors duration-150 ${filter === f ? 'bg-ink text-white' : 'bg-canvas text-muted hover:text-ink'}`} data-id="element-431" data-mp="n9os2ytdp2of">
              {f}
            </button>)}
        </div>
        <motion.ul layout className="mt-5 grid grid-cols-1 gap-3 sm:grid-cols-3" data-id="element-432" data-mp="2b9aikdynyxcq">
          <AnimatePresence mode="popLayout" initial={false} data-id="element-433" data-mp="38uerz3qm413d">
            {visible.map(e => <motion.li key={e.id} layout initial={{
            opacity: 0,
            scale: 0.96
          }} animate={{
            opacity: 1,
            scale: 1
          }} exit={{
            opacity: 0,
            scale: 0.96
          }} transition={{
            duration: 0.22,
            ease
          }} className="flex items-center gap-3 rounded-2xl bg-canvas p-2.5 sm:block sm:p-0 sm:pb-3" data-id="element-434" data-mp="33xept3wvvihj">
                <img src={e.image} alt="" className="leaf-sm h-16 w-16 object-cover sm:h-28 sm:w-full" data-id="element-435" data-mp="1jfqkngwvp9c7" />
                <div className="sm:px-3 sm:pt-3" data-id="element-436" data-mp="3v5g8ilore7j2">
                  <p className="text-xs text-muted" data-id="element-437" data-mp="2zt4w62nr544">
                    {e.date} · {e.city}
                  </p>
                  <p className="text-sm font-semibold text-ink" data-id="element-438" data-mp="7ynp9cg9x1qc">{e.title}</p>
                </div>
              </motion.li>)}
          </AnimatePresence>
        </motion.ul>
      </div>
    </article>;
}
function SpeedTile() {
  const segments = ['Scan', 'Play', 'Confirmed'];
  return <article className="leaf-md flex flex-col bg-forest p-7 text-white lg:p-9" data-id="element-439" data-mp="mnk0tjhjgwvp">
      <h3 className="font-display text-2xl font-bold tracking-tight" data-id="element-440" data-mp="qpwwff2kneer">Join in seconds</h3>
      <p className="mt-2 text-white/70" data-id="element-441" data-mp="vorpd9u96jcl">No unnecessary sign-up. From scan to confirmed entry, fast.</p>
      <div className="mt-auto pt-10" data-id="element-442" data-mp="2sh1noa0lecyk">
        <p className="font-display text-6xl font-bold tracking-[-0.04em] text-mint" data-id="element-443" data-mp="2si38v7bbofu0">
          &lt;90<span className="ml-2 text-xl font-semibold tracking-normal text-white/70" data-id="element-444" data-mp="2xfbksq5vnnmt">sec</span>
        </p>
        <div className="mt-6 grid grid-cols-3 gap-1.5" data-id="element-445" data-mp="224wxlfea1qjr">
          {segments.map((s, i) => <div key={s} data-id="element-446" data-mp="1x5tpfm30347k" data-mpi={i}>
              <div className="h-1.5 overflow-hidden rounded-full bg-white/15" data-id="element-447" data-mp="1x6bxrh2xt8vu">
                <motion.div className="h-full origin-left rounded-full bg-mint" initial={{
              scaleX: 0
            }} whileInView={{
              scaleX: 1
            }} viewport={{
              once: true,
              amount: 0.8
            }} transition={{
              duration: 0.3,
              ease,
              delay: 0.2 + i * 0.3
            }} data-id="element-448" data-mp="2c32t9f9hq0g6" />
              </div>
              <p className="mt-2 text-xs font-medium text-white/60" data-id="element-449" data-mp="1t59tglu5y6n0">{s}</p>
            </div>)}
        </div>
      </div>
    </article>;
}
function RewardsTile() {
  const rows = ['Rules published before start', 'Entries checked', 'Winner selected and informed'];
  return <article className="leaf-md flex flex-col bg-mint-soft p-7 lg:p-9" data-id="element-450" data-mp="10mmc9hiafbr7">
      <h3 className="font-display text-2xl font-bold tracking-tight text-ink" data-id="element-451" data-mp="vpcp68idfc6j">Rewards you can trust</h3>
      <p className="mt-2 text-muted" data-id="element-452" data-mp="37bwpw3l6nikb">Prizes from the brands behind the event, drawn by rules fixed in advance.</p>
      <ul className="mt-auto space-y-2 pt-8" data-id="element-453" data-mp="3c60z6thx82ok">
        {rows.map((r, i) => <li key={r} className="flex items-center gap-3 rounded-xl bg-white px-3.5 py-3 text-sm font-medium text-ink" data-id="element-454" data-mp="2xgmpfkafnj8n" data-mpi={i}>
            <motion.span initial={{
          opacity: 0,
          scale: 0.6
        }} whileInView={{
          opacity: 1,
          scale: 1
        }} viewport={{
          once: true,
          amount: 0.8
        }} transition={{
          duration: 0.2,
          ease,
          delay: 0.15 + i * 0.12
        }} className="grid h-6 w-6 shrink-0 place-items-center rounded-full bg-forest" data-id="element-455" data-mp="32c1w9sa0wevk">
              <CheckIcon className="h-3.5 w-3.5 text-mint" strokeWidth={3} data-id="element-456" data-mp="3l3b9abj05lrw" />
            </motion.span>
            {r}
          </li>)}
      </ul>
    </article>;
}
function CheckInTile() {
  return <article className="leaf-md grid items-center gap-8 bg-white p-7 ring-1 ring-line sm:grid-cols-[1fr_auto] lg:col-span-2 lg:p-9" data-id="element-457" data-mp="29r0r0z1vx81p">
      <div data-id="element-458" data-mp="g8kzaszgmdk9">
        <h3 className="font-display text-2xl font-bold tracking-tight text-ink" data-id="element-459" data-mp="b7xa8cusk6so">Check in with a scan</h3>
        <p className="mt-2 max-w-md text-muted" data-id="element-460" data-mp="6gno3m8gbllj">
          Scan the QR code at the venue to unlock the event's actions. It's optional, and your location is never
          tracked continuously.
        </p>
        <p className="mt-6 inline-flex items-center gap-2 rounded-full bg-canvas px-3.5 py-2 text-sm font-semibold text-forest" data-id="element-461" data-mp="3tm68w5669h73">
          <LocateOffIcon className="h-4 w-4" data-id="element-462" data-mp="3olqx3i1jghr8" />
          QR, not GPS
        </p>
      </div>
      <div className="relative mx-auto grid h-48 w-48 place-items-center overflow-hidden rounded-3xl bg-forest-deep" data-id="element-463" data-mp="3joian59cxrk6">
        <span className="absolute left-4 top-4 h-7 w-7 rounded-tl-xl border-l-[3px] border-t-[3px] border-mint" data-id="element-464" data-mp="3epxbl95uddxb" />
        <span className="absolute right-4 top-4 h-7 w-7 rounded-tr-xl border-r-[3px] border-t-[3px] border-mint" data-id="element-465" data-mp="1htzua3roogav" />
        <span className="absolute bottom-4 left-4 h-7 w-7 rounded-bl-xl border-b-[3px] border-l-[3px] border-mint" data-id="element-466" data-mp="1cv6cw09a055f" />
        <span className="absolute bottom-4 right-4 h-7 w-7 rounded-br-xl border-b-[3px] border-r-[3px] border-mint" data-id="element-467" data-mp="1pfd76du6fbzo" />
        <QrCode size={112} color="#FFFFFF" data-id="element-468" data-mp="1u61y9s1y26ya" />
        <span className="animate-scan absolute inset-x-6 h-0.5 rounded-full bg-mint shadow-[0_0_12px_2px_rgba(142,235,193,0.6)]" data-id="element-469" data-mp="1u8ep6upojrnh" />
      </div>
    </article>;
}